import * as React from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { ChevronUp, LogOut } from "lucide-react";
import mark from "./claymore-thistle-mark.png";
import { useCurrentUser, useSignOut } from "../../backend/queries/auth";
import { Avatar, AvatarFallback } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from "../ui/sidebar";
import {
  NAVIGATION_PLACEHOLDER_COUNT,
  accountNavigationForRole,
  groupedNavigationForRole,
  isNavItemActive,
} from "./navigation";

function initialsFor(user: { firstname?: string; lastname?: string; email?: string } | null) {
  const first = user?.firstname?.[0] || user?.email?.[0] || "U";
  const last = user?.lastname?.[0] || "";
  return `${first}${last}`.toUpperCase();
}

export type AppSidebarProps = {
  /** Product name shown in the sidebar header. */
  productName?: string;
};

/**
 * The role-aware navigation rail.
 *
 * While the role is still loading it renders a fixed number of placeholder
 * rows: the shell keeps its size, and no entry appears that the finished role
 * is not allowed to see.
 */
export function AppSidebar({ productName = "Claymore Thistle" }: AppSidebarProps) {
  const { data: currentUser, isPending } = useCurrentUser();
  const signOut = useSignOut();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpenMobile, isMobile, state } = useSidebar();
  const [signOutError, setSignOutError] = React.useState("");

  const user = currentUser?.profile || currentUser || null;
  const role = currentUser?.role || currentUser?.profile?.role || null;

  const sections = React.useMemo(
    () => groupedNavigationForRole(role),
    [role],
  );
  const accountItems = React.useMemo(
    () => accountNavigationForRole(role),
    [role],
  );
  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(" ");

  // Following a link inside the mobile drawer should close it.
  const closeOnMobile = () => {
    if (isMobile) setOpenMobile(false);
  };

  const handleSignOut = async () => {
    setSignOutError("");
    try {
      await signOut.mutateAsync();
    } catch {
      setSignOutError("Sign out failed. Try again.");
      return;
    }
    navigate("/login");
  };

  return (
    <Sidebar variant="inset" collapsible="icon">
      <SidebarHeader>
        <div className="flex h-8 items-center gap-2 px-2 group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0">
          {/*
            Decorative: the wordmark beside it already names the product, so the
            image is hidden from assistive technology rather than announced
            twice. The artwork is black, so it inverts on a dark rail.
          */}
          <img
            src={mark}
            alt=""
            aria-hidden="true"
            className="h-6 w-6 shrink-0 object-contain group-data-[collapsible=icon]:hidden dark:invert"
          />
          <span className="truncate text-sm font-semibold group-data-[collapsible=icon]:hidden">
            {productName}
          </span>
          {/*
            The visible way to collapse, expand, or close the rail. The
            keyboard shortcut is a shortcut, not the only route: a tablet
            starts collapsed and may have no keyboard at all.
          */}
          <SidebarTrigger
            className="ml-auto min-h-target min-w-target group-data-[collapsible=icon]:ml-0"
            aria-label={
              isMobile
                ? "Close navigation"
                : state === "expanded"
                  ? "Collapse navigation"
                  : "Expand navigation"
            }
          />
        </div>
      </SidebarHeader>

      {/*
        The rail is the primary navigation landmark, so screen-reader users can
        jump straight to it from anywhere in the shell.
      */}
      <SidebarContent aria-label="Primary" role="navigation">
        {isPending ? (
          <SidebarGroup>
            <SidebarGroupLabel>Workspace</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu aria-busy="true" aria-label="Loading navigation">
                {Array.from({ length: NAVIGATION_PLACEHOLDER_COUNT }, (_, index) => (
                  <SidebarMenuItem key={index}>
                    <SidebarMenuSkeleton showIcon />
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ) : (
          sections.map((section) => (
            <SidebarGroup key={section.group}>
              <SidebarGroupLabel>{section.group}</SidebarGroupLabel>
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => {
                    const Icon = item.icon;
                    const active = isNavItemActive(item, location.pathname);

                    return (
                      <SidebarMenuItem key={item.to}>
                        <SidebarMenuButton
                          asChild
                          isActive={active}
                          tooltip={item.label}
                        >
                          <NavLink
                            to={item.to}
                            onClick={closeOnMobile}
                            aria-current={active ? "page" : undefined}
                          >
                            <Icon />
                            <span>{item.label}</span>
                          </NavLink>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  })}
                </SidebarMenu>
              </SidebarGroupContent>
            </SidebarGroup>
          ))
        )}
      </SidebarContent>

      <SidebarFooter>
        {signOutError && (
          <p
            role="alert"
            className="rounded-md border border-tone-danger-border bg-tone-danger-surface px-2 py-1.5 text-xs text-tone-danger-foreground group-data-[collapsible=icon]:hidden"
          >
            {signOutError}
          </p>
        )}

        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  className="group-data-[collapsible=icon]:justify-center"
                  aria-label={`Account menu for ${fullName || "your account"}`}
                >
                  <Avatar className="h-8 w-8 shrink-0 rounded-lg">
                    <AvatarFallback className="rounded-lg">
                      {initialsFor(user)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0 flex-1 text-left group-data-[collapsible=icon]:hidden">
                    <span className="block truncate font-medium">
                      {isPending ? "Loading" : fullName || "User"}
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {role || "Account"}
                    </span>
                  </span>
                  <ChevronUp className="ml-auto group-data-[collapsible=icon]:hidden" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              {/*
                The trigger sits at the bottom of the rail, so the menu rises
                above it and stays inside the rail's column rather than
                overlapping the page.
              */}
              <DropdownMenuContent
                side="top"
                align="start"
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56"
              >
                <DropdownMenuLabel>
                  <span className="block truncate">{fullName || "Account"}</span>
                  <span className="block truncate text-xs font-normal text-muted-foreground">
                    {user?.email || "Signed in"}
                  </span>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {accountItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <DropdownMenuItem
                      key={`${item.to}-${item.label}`}
                      onSelect={() => {
                        closeOnMobile();
                        navigate(item.to);
                      }}
                    >
                      <Icon className="h-4 w-4" aria-hidden="true" />
                      {item.label}
                    </DropdownMenuItem>
                  );
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={handleSignOut}
                  className="text-tone-danger-foreground"
                >
                  <LogOut className="h-4 w-4" aria-hidden="true" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
      <SidebarRail aria-label="Toggle navigation" />
    </Sidebar>
  );
}
